import React, { useEffect, useState } from 'react';
import { api } from '../../services/api';
import type { GeoPoint } from '../../types/deliveryTour';

interface PlaceRow {
  id: string;
  label: string;
  street: string;
  street_number: string;
  postal_code: string;
  city: string;
  latitude: number;
  longitude: number;
}

interface EndPlaceSelectProps {
  value: GeoPoint | null;
  onChange: (point: GeoPoint | null) => void;
}

function toPoint(place: PlaceRow): GeoPoint {
  return {
    latitude: Number(place.latitude),
    longitude: Number(place.longitude),
    label: place.label,
    address: `${place.street} ${place.street_number}, ${place.postal_code} ${place.city}`,
  };
}

export const EndPlaceSelect: React.FC<EndPlaceSelectProps> = ({
  value,
  onChange,
}) => {
  const [places, setPlaces] = useState<PlaceRow[]>([]);

  useEffect(() => {
    void api
      .get('/users/me/places')
      .then((res) => setPlaces(res.data || []))
      .catch(() => setPlaces([]));
  }, []);

  const selected = value?.label;

  return (
    <div className="space-y-2">
      <p className="text-xs font-black uppercase tracking-widest text-gray-400">
        End (optional)
      </p>
      <p className="text-sm text-gray-600">
        Saved place after the last store, or skip.
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onChange(null)}
          className={`px-3 py-2 rounded-xl border text-sm font-bold min-h-[40px] ${
            !value
              ? 'border-gray-900 bg-gray-900 text-white'
              : 'border-gray-200 bg-white text-gray-700'
          }`}
        >
          None
        </button>
        {places.map((place) => (
          <button
            key={place.id}
            type="button"
            onClick={() => onChange(toPoint(place))}
            className={`px-3 py-2 rounded-xl border text-sm font-bold min-h-[40px] ${
              selected === place.label
                ? 'border-gray-900 bg-gray-900 text-white'
                : 'border-gray-200 bg-white text-gray-700'
            }`}
          >
            {place.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export function readCurrentLocation(): Promise<GeoPoint> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('GPS is not available'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          label: 'Current location',
        }),
      () => reject(new Error('Could not read GPS'))
    );
  });
}
